import React, { memo, useMemo } from 'react';
import { defaultTheme } from '@styles/theme.styled';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import AnimatedElement from '@components/animated-element';
import { Wrapper } from '@components/wrapper.styled';
import {
  TitleContainer,
  ImageContainer,
  StyledLinkComponent,
  TextContent,
  FeatureWrapper,
  StyledImage,
  StyledText,
  StyledItemText,
  FeatureItemWrapper,
  FeatureListWrapper,
  FeatureListIcon,
  FeatureListDescription,
  IconWrapper,
  EmbedContainer,
  OverflowContainer,
  StyledSection,
} from './feature.styled';

export interface FeatureItemProps {
  icon?: ImageProps;
  description?: {
    richText: RichTextBlock[];
    text: string;
  };
}

export interface FeatureProps {
  embed?: {
    html?: string;
    embed_url?: string;
    url?: string;
  };
  image?: ImageProps;
  imageFit?: boolean;
  blendImage?: boolean;
  imageAlignment?: 'default' | 'left' | 'right';
  imageCrop?: 'none' | 'bottom' | 'top' | 'left' | 'right';
  title?: string;
  subheading?: RichTextBlock[];
  description?: RichTextBlock[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  link?: {
    uid?: string;
    url?: string;
  };
  linkLabel?: string;
  'aria-label'?: string;
  items: FeatureItemProps[];
}

const Feature: React.FC<FeatureProps> = ({
  embed,
  image,
  imageFit,
  blendImage,
  title,
  subheading,
  description,
  link,
  linkLabel,
  backgroundColor = 'white',
  imageAlignment = 'default',
  items,
  'aria-label': ariaLabel,
  imageCrop = 'none',
}) => {
  const linkConfig = getLinkConfig(link);
  const embedUrl = useMemo(() => {
    if (embed?.html) {
      const match = embed.html.match(/src=["']([^"']+)["']/i);
      if (match?.[1]) {
        return match[1];
      }
    }

    return embed?.embed_url || embed?.url;
  }, [embed?.embed_url, embed?.html, embed?.url]);

  const hasEmbed = !!embedUrl;
  const hasImage = !!(image?.gatsbyImageData || image?.url);
  const hasSubheading = !!subheading?.length;
  const hasDescription = !!description?.length;
  const hasLink = !!linkConfig.url;
  const hasTitleOrSubheading = !!(title || hasSubheading);

  const FeatureList = useMemo(() => {
    return (
      <FeatureListWrapper itemCount={items.length}>
        {items.map(
          (item) =>
            item.icon?.url && (
              <FeatureItemWrapper key={`feature-list-${item.description?.richText[0].text || ''}`}>
                <IconWrapper>
                  <FeatureListIcon
                    url={item.icon.url}
                    alt={item.icon.alt}
                    gatsbyImageData={item.icon.gatsbyImageData}
                    loading="lazy"
                    objectFit="contain"
                  />
                </IconWrapper>
                <FeatureListDescription>
                  <StyledItemText
                    type="description"
                    richText={item.description?.richText}
                    margin="none"
                    fontSize="body"
                  />
                </FeatureListDescription>
              </FeatureItemWrapper>
            )
        )}
      </FeatureListWrapper>
    );
  }, [items]);

  return (
    <StyledSection backgroundColor={backgroundColor} hasEmbed={hasEmbed}>
      <Wrapper>
        <FeatureWrapper
          role="article"
          $imageAlignment={imageAlignment}
          aria-label={ariaLabel || title}
          hasEmbed={hasEmbed}
          hasImage={hasImage}
        >
          <TextContent hasItems={items.length > 0} hasEmbed={hasEmbed} hasImage={hasImage}>
            {hasTitleOrSubheading && (
              <TitleContainer hasEmbed={hasEmbed} hasImage={hasImage}>
                {title && (
                  <Text
                    tag="h2"
                    type="title"
                    stringText={title}
                    fontSize="heading3"
                    margin="none"
                    fontColor={backgroundColor === 'dark' ? 'white' : 'secondary'}
                  />
                )}
                {hasSubheading && (
                  <Text
                    type="subheading"
                    richText={subheading}
                    margin="none"
                    fontColor={backgroundColor === 'dark' ? 'white' : 'secondary'}
                  />
                )}
              </TitleContainer>
            )}
            {hasDescription && (
              <StyledText
                type="description"
                richText={description}
                margin="none"
                fontColor={backgroundColor === 'dark' ? 'white' : 'dark'}
              />
            )}
            {items.length > 1 && FeatureList}
            {hasLink && (
              <StyledLinkComponent
                displayArrow={false}
                type="button"
                isExternal={linkConfig.isExternal}
                url={linkConfig.url}
                title={linkLabel || 'Explore all products and features'}
                aria-label={linkLabel || 'Explore all products and features'}
              />
            )}
          </TextContent>
          {hasEmbed ? (
            <AnimatedElement
              animationType="slideInRight"
              delay={0.125}
              threshold={0.125}
              rootMargin="0px"
              width="100%"
            >
              <OverflowContainer>
                <div style={{ width: '100%', zIndex: 1, position: 'relative' }}>
                  <EmbedContainer
                    src={embedUrl}
                    title={ariaLabel || title || 'Embedded media'}
                    aria-label={ariaLabel || title || 'Embedded media'}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </OverflowContainer>
            </AnimatedElement>
          ) : (
            hasImage && (
              <AnimatedElement
                animationType={imageAlignment === 'left' ? 'slideInRight' : 'slideInLeft'}
                delay={0.125}
                threshold={0.125}
                rootMargin="0px"
                width="100%"
              >
                <ImageContainer
                  imageFit={imageFit}
                  imageCrop={imageCrop}
                  backgroundColor={backgroundColor}
                >
                  <StyledImage
                    url={image?.url || ''}
                    alt={image?.alt || title || 'Feature image'}
                    gatsbyImageData={image?.gatsbyImageData}
                    loading="lazy"
                    objectPosition={items.length > 0 ? '0% 50%' : '50% 50%'}
                    objectFit={imageFit ? 'cover' : 'contain'}
                    blendImage={blendImage}
                    imageCrop={imageCrop}
                  />
                </ImageContainer>
              </AnimatedElement>
            )
          )}
        </FeatureWrapper>
      </Wrapper>
    </StyledSection>
  );
};

export default memo(Feature);
